<template>
  <page-header title="Plugins">
    <router-link
      to="/plugin"
      class="btn-primary ml-auto h-10"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </router-link>
  </page-header>

  <grid-items-empty v-if="isEmpty && search === null" link="/plugin" />

  <template v-else>
    <div class="flex justify-between mb-4">
      <input-search v-model="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getPlugins"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @delete="onDelete"
      @enable="onEnable"
      @disable="onDisable"
    />
  </template>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import PluginListActions from '../components/PluginListActions.vue';
import Grid from '../components/grid/Grid.vue';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const toast = useToast();

function rowClass(_row, index) {
  return (index % 2 ? 'bg-zinc-300/10' : '') + (!_row.isEnabled ? ' [&>td:not(:last-child)]:!opacity-30' : ''); // stripes class for row
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value || '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/plugin/?${searchParams}`;
}

async function getPlugins(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;

  return {
    total: data.value.total,
    rows: data.value.plugins
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    titleClass: '', // class for title cell
    sortable: true
  },
  {
    title: 'Name',
    prop: 'name',
    click: row => router.push(`/plugin/${row.id}`),
    sortable: true
  },
  {
    title: 'AppID',
    value: row => row.automationAppId
  },
  {
    title: 'Programs',
    class: 'text-elipsis max-w-xl',
    value: row => (row.Programs || []).map(p => p.name).join(', ') || '—'
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(PluginListActions)
  }
];

async function onDelete(row) {
  const confirm = await Modal.confirm({
    title: 'Delete Plugin',
    body: `Delete the plugin "${row.name}"?`,
    okButton: 'Delete',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/plugin/${row.id}`).delete().json();

  if (data.value?.success) {
    toast.success('Plugin deleted successfully');
    reloadGrid();
    return;
  }

  toast.error('Error deleting plugin');
}

async function onEnable(row) {
  const { data } = await useFetch(`${apiDomain}/api/plugin/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Plugin enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling plugin');
}

async function onDisable(row) {
  const { data } = await useFetch(`${apiDomain}/api/plugin/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Plugin disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling plugin');
}
</script>
